import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { aiApi, isAiGenerationCancelled } from '@/api/ai';
import { SearchNormal1, Cross } from '@/components/icons';
import { Input } from '@/components/ui/input';
import { useModalContext } from '@/components/ui/modal-provider';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import BlockWithShowMore from '../preview/BlockWithShowMore';
import AIGeneratorCard from './AIGeneratorCard';
import { getCidLabel } from './cid-options';
import CIDSearchModal from './CIDSearchModal';
import { SECTIONS } from './constants';
import { WizardStepProps } from './types';
import type { SourceActionsMap } from './AIDataSources';
import type { CIDOption } from '@/types/report';

export default function DiagnosisStep({ data, onUpdate }: WizardStepProps) {
    const { openWizardModal } = useModalContext();
    const {
        silentSave,
        handleSectionClick,
        sectionRequirements,
        setIsGenerating,
        setGeneratingSectionId,
        beginGeneration,
        endGeneration,
    } = useReportWizard();

    // O rótulo é a frase inteira: verbo e substantivo, porque o botão é a
    // única coisa escrita ali. "Ir para História Clínica" dizia para onde, não
    // o que falta — e o painel não tem mais uma linha ao lado para completar.
    const sourceActions = useMemo<SourceActionsMap>(
        () => ({
            clinical_history_content: {
                label: 'Escrever a História Clínica',
                onClick: () => handleSectionClick(SECTIONS.CLINICAL_HISTORY, 'shortcut_card'),
            },
            tests_results: {
                label: 'Adicionar resultados de instrumentos',
                onClick: () => handleSectionClick(SECTIONS.TESTS, 'shortcut_card'),
            },
            test_table_block: {
                label: 'Adicionar a tabela de instrumentos',
                onClick: () => handleSectionClick(SECTIONS.TESTS, 'shortcut_card'),
            },
        }),
        [handleSectionClick],
    );

    const [selectedCIDs, setSelectedCIDs] = useState<CIDOption[]>(
        (data.selected_cids || []).map((id: string) => ({
            id,
            label: getCidLabel(id),
        })),
    );

    useEffect(() => {
        setSelectedCIDs(
            (data.selected_cids || []).map((id: string) => ({
                id,
                label: getCidLabel(id),
            })),
        );
    }, [data.selected_cids]);

    const applySelectedCIDs = (newSelected: CIDOption[]) => {
        setSelectedCIDs(newSelected);
        onUpdate({ selected_cids: newSelected.map((c) => c.id) });
    };

    const removeCID = (cidId: string) => {
        const newSelected = selectedCIDs.filter((c) => c.id !== cidId);
        applySelectedCIDs(newSelected);
    };

    const handleOpenCIDModal = () => {
        openWizardModal({
            title: 'Adicionar CIDs',
            content: <CIDSearchModal selectedCIDs={selectedCIDs} onApply={applySelectedCIDs} />,
            size: 'lg',
            className: 'max-h-[90vh]',
        });
    };

    const handleGenerate = async () => {
        if (!data.id) return;

        setIsGenerating(true);
        setGeneratingSectionId(SECTIONS.DIAGNOSIS);
        const generation = beginGeneration();
        await silentSave();
        try {
            const response = await aiApi.generate(
                data.id,
                'diagnosis',
                {
                    cid_codes: selectedCIDs.map((c) => c.id),
                    extra_details: data.diagnosis_details || '',
                },
                'create',
                undefined,
                generation.options,
            );
            onUpdate((prevData) => ({
                diagnosis: {
                    ...prevData.diagnosis,
                    content: response.content,
                },
            }));
            toast.success('Rascunho da conclusão gerado. Revise no laudo ao lado.');
        } catch (error: unknown) {
            // The user stopped it. The section is untouched and no credit was
            // spent, and the button they pressed is answer enough.
            if (isAiGenerationCancelled(error)) return;

            toast.error((error as Error).message || 'Erro ao gerar conteúdo.');
        } finally {
            setIsGenerating(false);
            setGeneratingSectionId(null);
            endGeneration();
        }
    };

    return (
        <div className="flex h-full flex-col">
            <AIGeneratorCard
                onGenerate={handleGenerate}
                sourceStepId={SECTIONS.DIAGNOSIS}
                reportData={data}
                sourceActions={sourceActions}
                backendRequirements={sectionRequirements}
            >
                {/* No bottom margin: the shell is a flex column with its own
                    gap now, so one would stack on top of the other. */}
                <div className="relative">
                    <SearchNormal1 className="absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        readOnly
                        placeholder="Busque pelo CID"
                        value=""
                        onClick={handleOpenCIDModal}
                        onFocus={handleOpenCIDModal}
                        className="cursor-pointer pl-9"
                    />
                </div>

                {/* The list unmounts with its contents. Kept mounted on an
                    empty selection it was a zero-height flex item that still
                    took a gap on either side — a band of dead space between the
                    field and the button, explaining nothing. The CID is
                    optional, so there is nothing to say in its place. */}
                {selectedCIDs.length > 0 && (
                    <BlockWithShowMore compareAgainst={selectedCIDs.length}>
                        <div className="flex flex-wrap gap-2">
                            {selectedCIDs.map((cid) => {
                                return (
                                    <div
                                        key={cid.id}
                                        className="flex items-center gap-1 rounded-full border border-border bg-muted px-3 py-1.5 text-sm"
                                    >
                                        <span>{cid.label}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeCID(cid.id)}
                                            className="text-muted-foreground hover:text-muted-foreground"
                                        >
                                            <Cross className="size-3" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </BlockWithShowMore>
                )}
            </AIGeneratorCard>
        </div>
    );
}
